import { useLogger } from "@amzn/xb-hooks";
import { useConfig } from "./useConfig";

// prepend the string with "." to keep things consistent across apps
export type ShortbreadCookieDomain = `.${string}` | "localhost";

export interface ShortbreadConfig {
  shortbread: {
    /**
     * What domain should shortbread use for this stage?
     *
     * Warning {@link https://tiny.amazon.com/42hkxwcv/shortbreaddomains}:
     * -------------------------------------------------------------------
     * The domain argument that you pass to getShortbreadInstance() must match
     * the domain of your application, or Shortbread will throw an error.
     * Additionally, when your application checks Shortbread for the customer's
     * cookie preferences, Shortbread will only return the default cookie preference,
     * essential cookies only.
     *
     * Shortbread validates the domain that you pass in by checking if it matches
     * the end of the domain name retrieved via window.location.hostname.
     *
     * Implementation Note: Preference is given to items listed earlier in the list.
     * E.G. ['.aws.dev', '.beta.skillbuilder.training.aws.dev'] resolves to '.aws.dev'
     * when rendering on 'beta.skillbuilder.aws'.
     */
    allowedCookieDomains: ShortbreadCookieDomain[];
  };
}

const unDot = (dotted: string) => (dotted.startsWith(".") ? dotted.slice(1) : dotted);
export const useShortbreadDomain = () => {
  const {
    shortbread: { allowedCookieDomains },
  } = useConfig();
  const { fatal } = useLogger();

  const { hostname } = window.location;
  const domain = allowedCookieDomains.find((allowed) => hostname.includes(unDot(allowed)));

  if (domain === undefined) {
    /**
     * This is a serious issue.
     *
     * This domain is what shortbread uses to set the customer cookie preferences (as a cookie).
     * If this is configured incorrectly it can create duplicate cookies preference cookie which
     * results in unexpected behaviors.
     */
    fatal("Hostname does not match any configured shortbread domains.");
  }

  return domain || hostname; // we _can_ set a cookie with hostname so is a better fallback than undefined
};
